import {
    createContext,
    useContext,
    createEffect,
    createSignal,
    on,
} from 'solid-js'
import { capture as posthogCapture } from '../../utilities/use-posthog.js'
import SessionService from '../services/session-service.js'
import { closeAuthModalWhenSessionArrives } from '../utils/auth-modal-session-arrival.js'
import { buildSignInRedirectPath } from '../utils/sign-in-redirect.js'

const AuthModalContext = createContext()

export function AuthModalProvider (props) {
    const [
        showAuthModal,
        setShowAuthModal,
    ] = createSignal(false)
    const [
        redirectUrl,
        setRedirectUrl,
    ] = createSignal(null)
    const [
        toSignUp,
        setToSignUp,
    ] = createSignal(false)
    const [
        variant,
        setVariant,
    ] = createSignal(null)
    const [
        bookingIntentToken,
        setBookingIntentToken,
    ] = createSignal(null)
    // TENANT-5533: acquisition entry point (e.g. 'qr') the opener wants
    // threaded through the OAuth chain — same transport as bookingIntentToken.
    const [
        onboardingSource,
        setOnboardingSource,
    ] = createSignal(null)
    // TENANT-5533: a non-dismissible open (no close control, no backdrop or
    // Escape close) for entry points where the modal IS the page.
    const [
        noClose,
        setNoClose,
    ] = createSignal(false)

    const api = {
        bookingIntentToken,
        closeAuthModal: () => {
            setShowAuthModal(false)
            setBookingIntentToken(null)
            setOnboardingSource(null)
            setNoClose(false)
            setRedirectUrl(null)
            setVariant(null)
        },
        noClose,
        onboardingSource,
        // `triggerSource` is the optional 5th param that tags the PostHog
        // `auth_modal_opened` event with the entry surface. Recommended values:
        // 'header_login_button', 'header_signup_button', 'book_now',
        // 'become_host', 'protected_route_redirect', 'profile_action',
        // 'partner_welcome', 'savings_page', 'featured_destination',
        // 'referral_page', 'login_query_param', 'terms_modal', 'session_expired', 'other'.
        // Callers that don't pass it get 'other'. The event is fired exactly
        // once per modal open, regardless of which button or page triggered it.
        // TENANT-5533: optional 6th param `onboardingSource` (e.g. 'qr') is
        // forwarded on every authorize the modal builds (log in, Google, sign
        // up) so the API can stamp the profile. Callers that don't pass it
        // send nothing. Optional 7th `options.noClose` renders the modal
        // without any way to dismiss it (used by /join, where the modal is
        // the whole page).
        openAuthModal: (url = null, toSignUp = false, variant = null, bookingIntentToken = null, triggerSource = null, onboardingSource = null, options = {}) => {
            setRedirectUrl(url)
            setShowAuthModal(true)
            setToSignUp(toSignUp)
            setVariant(variant)
            setBookingIntentToken(bookingIntentToken)
            setOnboardingSource(onboardingSource)
            setNoClose(options.noClose === true)
            posthogCapture('auth_modal_opened', {
                // TENANT-5533: allowlisted entry source (e.g. 'qr'), or null.
                onboarding_source: onboardingSource ?? null,
                trigger_source: triggerSource ?? 'other',
            })
        },
        redirectUrl,
        // Pre-set the post-login redirect URL without opening the modal. Used by pages
        // (e.g. listing page) that want the header "Log in" button to return the user
        // to the current page after authentication.
        setPreRedirectUrl: (url) => setRedirectUrl(url),
        showAuthModal,
        toSignUp,
        variant,
    }

    // TENANT-5504: the API rejected the stored session and SessionService cleared it. ONE prompt
    // path per page: on a guarded page the existing login redirect (the guard's own session-loss
    // redirect stands down for this case), elsewhere the modal. A pending booking intent rides
    // along either way, so the stay request resumes after OAuth and owns the landing.
    createEffect(on(SessionService.signInPromptRequest, (request) => {
        if (!request) return
        const currentPath = `${window.location.pathname}${window.location.search}`
        if (SessionService.hasGuardedRoute) {
            window.location.replace(buildSignInRedirectPath({
                bookingIntentToken: request.bookingIntentToken,
                from: currentPath,
            }))
            return
        }
        if (request.bookingIntentToken) {
            api.openAuthModal(undefined, false, 'booking', request.bookingIntentToken, 'session_expired')
            return
        }
        api.openAuthModal(currentPath, false, undefined, undefined, 'session_expired')
    }, {
        defer: true,
    }))

    // TENANT-5814: a session pushed in from the extension (or another tab) while the modal is
    // open — typically the "session expired" prompt above after an extension-side logout —
    // dismisses it; the signed-in visitor must not be asked to log in.
    closeAuthModalWhenSessionArrives({
        bookingIntentToken,
        closeAuthModal: api.closeAuthModal,
        isAuthenticated: SessionService.isAuthenticated,
        noClose,
        showAuthModal,
    })

    return <AuthModalContext.Provider value={api}>{props.children}</AuthModalContext.Provider>
}

// Custom hook to use the AuthModalContext
export function useAuthModal () {
    const context = useContext(AuthModalContext)
    if (!context) {
        throw new Error('useAuthModal must be used within an AuthModalProvider')
    }
    return context
}
